/**
 * F-305 — Super Admin Entitlement Console API layer (T-252-001/T-252-002).
 *
 * Typed client for the F-303 backend console surface
 * (LP routes/admin-entitlements.js, mounted at /api/admin-entitlements,
 * requireSuperAdmin on every route; auth comes from the shared adminApi
 * interceptor). Display labels follow the human pricing directive: the free
 * tier is always shown as HOMESTEAD — never freemium/Free.
 *
 * CONTRACT FIX (prod blank-page regression, 2026-09-28): the first cut typed
 * the payload from the FE side and assumed `resolved` was an array of
 * entitlement rows — the real F-303 contract (LP lib/entitlements.js +
 * config/usd-entitlements.js, the source of truth) is:
 *
 *   GET /tenants            → data = RAW SETTINGS#ENTITLEMENTS store items
 *                             (no tenantId field — it lives in pk as
 *                             `TENANT#<id>`), premiumOverrides values are
 *                             BOOLEANS, plus count.
 *   GET /tenants/:tenantId  → data = { tenantId, record, resolved, effective }
 *                             record   = the raw store item (same shape as above)
 *                             resolved = ONE OBJECT: { tier, displayTierName,
 *                               subscriptionStatus, landlordClass, chargingBasis,
 *                               basisLabel, entitlements: string[],
 *                               premiumOverrides: {ENT_ID: boolean},
 *                               serviceUnits, unlimited, refundVoid }
 *                             effective = { allowance, basis, basisQuantity,
 *                               perBasis, unlimited, used, remaining, resetDate,
 *                               chargingBasis, tier }
 *   GET /catalog            → data = { entitlements: USD_ENTITLEMENTS[] (objects
 *                             keyed by `id`/`name`), landlordClasses: objects,
 *                             tiers, planOrder, chargingBases, tierConfigs:
 *                             getTierEntitlementConfig(tier)[] keyed by
 *                             `tier`/`entitlements` }
 *
 * Every payload this module returns is passed through a normalizer that
 * validates with Array.isArray/typeof guards, so any future contract drift
 * degrades to an empty/neutral state instead of throwing inside a render
 * (the exact failure mode that blanked /super-admin in prod).
 */

import { adminApi } from './api';

// ── Real backend shapes ──────────────────────────────────────────────────────

/** Raw SETTINGS#ENTITLEMENTS store item (backend premiumOverrides = boolean map). */
export interface EntitlementRecord {
  pk?: string;
  sk?: string;
  entityType?: string;
  tier: string;
  subscriptionStatus: string;
  landlordClass: string;
  premiumOverrides: Record<string, boolean>;
  refundVoid?: boolean;
  updatedAt?: string;
  updatedBy?: string;
}

/** The F-303 resolveEntitlements() return — ONE OBJECT, entitlements = string[] */
export interface ResolvedEntitlements {
  tier: string;
  displayTierName: string;
  subscriptionStatus: string;
  landlordClass: string;
  chargingBasis: 'property' | 'seats';
  basisLabel: string;
  entitlements: string[];
  premiumOverrides: Record<string, boolean>;
  serviceUnits: number;
  unlimited: boolean;
  refundVoid: boolean;
}

/** resolveEffectiveAllowance() return (allowance/remaining null when unlimited) */
export interface EffectiveAllowance {
  allowance: number | null;
  basis: string;
  basisQuantity: number;
  perBasis: number | null;
  unlimited: boolean;
  used: number;
  remaining: number | null;
  resetDate: string | null;
  chargingBasis: 'property' | 'seats';
  tier: string;
}

export interface TenantEntitlementDetail {
  tenantId: string;
  record: EntitlementRecord | null;
  resolved: ResolvedEntitlements | null;
  effective: EffectiveAllowance | null;
}

/** Normalized grid row (derived from raw store items). */
export interface TenantGridRow {
  tenantId: string;
  tier: string;
  subscriptionStatus: string;
  landlordClass: string;
  premiumOverrides: Record<string, boolean>;
  overriddenEntitlements: number;
  refundVoid: boolean;
  updatedAt: string | null;
}

/** USD_ENTITLEMENTS entry (catalog items are keyed `id`/`name`, planAccess is a STRING). */
export interface CatalogEntitlement {
  id: string;
  name: string;
  description: string;
  category: string;
  planAccess: string;
  extractionType: string;
  refundVoidTrigger: boolean;
  metered: boolean;
  featureRef?: string;
}

export interface LandlordClassDef {
  id: string;
  label: string;
  chargingBasis: 'property' | 'seats';
  basisLabel: string;
}

/** getTierEntitlementConfig(tier) entry (keyed `tier`/`entitlements`). */
export interface TierEntitlementConfig {
  tier: string;
  displayTierName: string;
  landlordClass: string;
  chargingBasis: 'property' | 'seats';
  basisLabel: string;
  entitlements: string[];
  serviceUnits: number;
  unlimited: boolean;
}

export interface EntitlementCatalog {
  entitlements: Record<string, CatalogEntitlement>;
  landlordClasses: LandlordClassDef[];
  tiers: string[];
  planOrder: string[];
  chargingBases: string[];
  tierConfigs: TierEntitlementConfig[];
}

export interface RefundVoidEntry {
  id: string;
  entitlementId: string | null;
  kind: string;
  note: string | null;
  createdAt: string;
  createdBy?: string;
}

// ── display naming (human directive: HOMESTEAD, never freemium/Free) ────────

export const TIER_LABELS: Record<string, string> = {
  freemium: 'HOMESTEAD',
  homestead: 'HOMESTEAD',
  essentials: 'Essentials',
  professional: 'Professional',
  enterprise: 'Enterprise',
};

export function tierLabel(tier?: string | null): string {
  if (!tier) return '—';
  return TIER_LABELS[String(tier).toLowerCase()] || String(tier);
}

export const STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  trial: 'Trial',
  trial_converting: 'Trial converting',
  cancelled: 'Cancelled',
  canceled: 'Cancelled',
  deactivated: 'Deactivated',
  past_due: 'Past due',
  restricted: 'Restricted',
};

export function statusLabel(status?: string | null): string {
  if (!status) return '—';
  return STATUS_LABELS[String(status).toLowerCase()] || String(status);
}

export function chargingBasisLabel(basis?: string | null): string {
  switch (String(basis || '').toLowerCase()) {
    case 'property': return 'Per PROPERTY (independent)';
    case 'seats': return 'Per USER seats';
    default: return basis || '—';
  }
}

// ── Normalizers (the crash-proofing layer) ───────────────────────────────────

/** Coerce anything into a real array: arrays pass through, object maps become
 *  their values, null/undefined/primitives become []. Every consumer-side
 *  .map/.filter/.find runs on this guarantee. */
export function ensureArray<T = unknown>(value: unknown): T[] {
  if (Array.isArray(value)) return value as T[];
  if (value && typeof value === 'object') return Object.values(value as Record<string, T>);
  return [];
}

const asString = (value: unknown, fallback = ''): string => {
  if (typeof value === 'string' && value) return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return fallback;
};

const asNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const TENANT_PK_PREFIX = 'TENANT#';

/** tenantId from a raw store item — falls back to parsing pk `TENANT#<id>`. */
function tenantIdFromRaw(raw: Record<string, unknown> | null): string {
  if (!raw) return '';
  const direct = asString(raw.tenantId);
  if (direct) return direct;
  const pk = asString(raw.pk);
  return pk.startsWith(TENANT_PK_PREFIX) ? pk.slice(TENANT_PK_PREFIX.length) : '';
}

/** Backend premiumOverrides values are booleans; tolerate {granted} shapes defensively. */
export function normalizePremiumOverrides(input: unknown): Record<string, boolean> {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return {};
  const out: Record<string, boolean> = {};
  for (const [entId, value] of Object.entries(input as Record<string, unknown>)) {
    if (typeof value === 'boolean') out[entId] = value;
    else if (value && typeof value === 'object' && typeof (value as any).granted === 'boolean') {
      out[entId] = (value as any).granted;
    } else if (value === 'true') out[entId] = true;
    else if (value === 'false') out[entId] = false;
  }
  return out;
}

/** resolved: the backend returns ONE OBJECT; tolerate legacy/foreign shapes
 *  (array of ids or {entitlementId} rows) by mapping them to the id list. */
export function normalizeResolved(input: unknown): ResolvedEntitlements | null {
  if (!input || typeof input !== 'object') return null;
  if (Array.isArray(input)) {
    const ids = (input as Array<unknown>)
      .map((x) => (typeof x === 'string' ? x : asString((x as any)?.entitlementId ?? (x as any)?.id)))
      .filter(Boolean);
    return {
      tier: 'freemium',
      displayTierName: '',
      subscriptionStatus: 'restricted',
      landlordClass: 'independent',
      chargingBasis: 'property',
      basisLabel: '',
      entitlements: ids,
      premiumOverrides: {},
      serviceUnits: 0,
      unlimited: false,
      refundVoid: false,
    };
  }
  const r = input as Record<string, unknown>;
  return {
    tier: asString(r.tier, 'freemium'),
    displayTierName: asString(r.displayTierName),
    subscriptionStatus: asString(r.subscriptionStatus, 'restricted'),
    landlordClass: asString(r.landlordClass, 'independent'),
    chargingBasis: r.chargingBasis === 'seats' ? 'seats' : 'property',
    basisLabel: asString(r.basisLabel),
    entitlements: ensureArray<unknown>(r.entitlements).filter((x): x is string => typeof x === 'string'),
    premiumOverrides: normalizePremiumOverrides(r.premiumOverrides),
    serviceUnits: asNumber(r.serviceUnits) ?? 0,
    unlimited: r.unlimited === true,
    refundVoid: r.refundVoid === true,
  };
}

export function normalizeEffective(input: unknown): EffectiveAllowance | null {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return null;
  const e = input as Record<string, unknown>;
  return {
    allowance: asNumber(e.allowance),
    basis: asString(e.basis),
    basisQuantity: asNumber(e.basisQuantity) ?? 0,
    perBasis: asNumber(e.perBasis),
    unlimited: e.unlimited === true,
    used: asNumber(e.used) ?? 0,
    remaining: asNumber(e.remaining),
    resetDate: typeof e.resetDate === 'string' ? e.resetDate : null,
    chargingBasis: e.chargingBasis === 'seats' ? 'seats' : 'property',
    tier: asString(e.tier),
  };
}

export function normalizeEntitlementRecord(input: unknown): EntitlementRecord | null {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return null;
  const raw = input as Record<string, unknown>;
  return {
    pk: typeof raw.pk === 'string' ? raw.pk : undefined,
    sk: typeof raw.sk === 'string' ? raw.sk : undefined,
    entityType: typeof raw.entityType === 'string' ? raw.entityType : undefined,
    tier: asString(raw.tier, 'freemium'),
    subscriptionStatus: asString(raw.subscriptionStatus, 'restricted'),
    landlordClass: asString(raw.landlordClass, 'independent'),
    premiumOverrides: normalizePremiumOverrides(raw.premiumOverrides),
    refundVoid: raw.refundVoid === true,
    updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : undefined,
    updatedBy: typeof raw.updatedBy === 'string' ? raw.updatedBy : undefined,
  };
}

export function normalizeGridRows(input: unknown): TenantGridRow[] {
  return ensureArray<Record<string, unknown>>(input)
    .map((raw) => {
      const premiumOverrides = normalizePremiumOverrides(raw?.premiumOverrides);
      return {
        tenantId: tenantIdFromRaw(raw),
        tier: asString(raw?.tier, 'freemium'),
        subscriptionStatus: asString(raw?.subscriptionStatus, 'restricted'),
        landlordClass: asString(raw?.landlordClass, 'independent'),
        premiumOverrides,
        overriddenEntitlements: Object.keys(premiumOverrides).length,
        refundVoid: raw?.refundVoid === true,
        updatedAt: typeof raw?.updatedAt === 'string' ? raw.updatedAt : null,
      };
    })
    .filter((r) => r.tenantId);
}

export function normalizeTenantDetail(input: unknown): TenantEntitlementDetail {
  const data = (input && typeof input === 'object' && !Array.isArray(input) ? input : {}) as Record<string, unknown>;
  const record = normalizeEntitlementRecord(data.record);
  return {
    tenantId: asString(data.tenantId) || tenantIdFromRaw(record ? (data.record as Record<string, unknown>) : null),
    record,
    resolved: normalizeResolved(data.resolved),
    effective: normalizeEffective(data.effective),
  };
}

export function normalizeCatalog(input: unknown): EntitlementCatalog {
  const c = (input && typeof input === 'object' && !Array.isArray(input) ? input : {}) as Record<string, unknown>;
  const entitlements: Record<string, CatalogEntitlement> = {};
  for (const raw of ensureArray<Record<string, unknown>>(c.entitlements)) {
    const id = asString(raw?.id ?? raw?.entitlementId);
    if (!id) continue;
    entitlements[id] = {
      id,
      name: asString(raw?.name ?? raw?.displayName, id),
      description: asString(raw?.description),
      category: asString(raw?.category),
      planAccess: asString(raw?.planAccess),
      extractionType: asString(raw?.extractionType),
      refundVoidTrigger: raw?.refundVoidTrigger === true,
      metered: raw?.metered === true,
      featureRef: typeof raw?.featureRef === 'string' ? raw.featureRef : undefined,
    };
  }
  const landlordClasses = ensureArray<Record<string, unknown>>(c.landlordClasses)
    .map((raw) => ({
      id: asString(raw?.id),
      label: asString(raw?.label, asString(raw?.id)),
      chargingBasis: (raw?.chargingBasis === 'seats' ? 'seats' : 'property') as 'property' | 'seats',
      basisLabel: asString(raw?.basisLabel),
    }))
    .filter((x) => x.id);
  const tierConfigs = ensureArray<Record<string, unknown>>(c.tierConfigs)
    .map((raw) => ({
      tier: asString(raw?.tier ?? raw?.tierId),
      displayTierName: asString(raw?.displayTierName),
      landlordClass: asString(raw?.landlordClass, 'independent'),
      chargingBasis: (raw?.chargingBasis === 'seats' ? 'seats' : 'property') as 'property' | 'seats',
      basisLabel: asString(raw?.basisLabel),
      entitlements: ensureArray<unknown>(raw?.entitlements ?? raw?.entitlementIds).filter(
        (x): x is string => typeof x === 'string'
      ),
      serviceUnits: asNumber(raw?.serviceUnits) ?? 0,
      unlimited: raw?.unlimited === true,
    }))
    .filter((t) => t.tier);
  return {
    entitlements,
    landlordClasses,
    tiers: ensureArray<unknown>(c.tiers).filter((x): x is string => typeof x === 'string'),
    planOrder: ensureArray<unknown>(c.planOrder).filter((x): x is string => typeof x === 'string'),
    chargingBases: ensureArray<unknown>(c.chargingBases).filter((x): x is string => typeof x === 'string'),
    tierConfigs,
  };
}

/** Uniform error for admin-entitlement mutations: carries HTTP status (409 =
 *  cross-tier grant rejection) and the backend message when present. */
export function overrideGrantError(err: unknown, fallback: string): Error & { status?: number } {
  const e = err as { response?: { status?: number; data?: { message?: string } }; message?: string } | null;
  const out = new Error(e?.response?.data?.message || e?.message || fallback) as Error & { status?: number };
  out.status = e?.response?.status;
  return out;
}

// ── API calls (F-303 /api/admin-entitlements contract) ──────────────────────

export const entitlementConsoleService = {
  getCatalog: async (): Promise<EntitlementCatalog> => {
    const res = await adminApi.get('api/admin-entitlements/catalog');
    return normalizeCatalog(res?.data?.data);
  },

  listTenants: async (): Promise<{ rows: TenantGridRow[]; count: number }> => {
    const res = await adminApi.get('api/admin-entitlements/tenants');
    const rows = normalizeGridRows(res?.data?.data);
    const count = asNumber(res?.data?.count) ?? rows.length;
    return { rows, count };
  },

  getTenantDetail: async (tenantId: string): Promise<TenantEntitlementDetail> => {
    const res = await adminApi.get(`api/admin-entitlements/tenants/${encodeURIComponent(tenantId)}`);
    return normalizeTenantDetail(res?.data?.data);
  },

  /** Grant/revoke a premium feature override. Throws via overrideGrantError
   *  semantics (status 409 = cross-tier grant rejected). */
  setEntitlementOverride: async (
    tenantId: string,
    entitlementId: string,
    granted: boolean,
    reason?: string
  ): Promise<{ record: EntitlementRecord | null; resolved: ResolvedEntitlements | null }> => {
    try {
      const res = await adminApi.put(
        `api/admin-entitlements/tenants/${encodeURIComponent(tenantId)}/entitlements/${encodeURIComponent(entitlementId)}`,
        { granted, reason: reason || null }
      );
      return normalizeTenantDetail({ ...res?.data?.data, tenantId });
    } catch (err) {
      throw overrideGrantError(err, 'Failed to update entitlement override');
    }
  },

  /** Remove the override entirely — fall back to the tier map. */
  clearEntitlementOverride: async (tenantId: string, entitlementId: string) => {
    const res = await adminApi.delete(
      `api/admin-entitlements/tenants/${encodeURIComponent(tenantId)}/entitlements/${encodeURIComponent(entitlementId)}`
    );
    return normalizeTenantDetail({ ...res?.data?.data, tenantId });
  },

  setTier: async (tenantId: string, tier: string, reason?: string) => {
    const res = await adminApi.put(
      `api/admin-entitlements/tenants/${encodeURIComponent(tenantId)}/tier`,
      { tier, reason: reason || null }
    );
    return normalizeTenantDetail({ ...res?.data?.data, tenantId });
  },

  setLandlordClass: async (tenantId: string, landlordClass: string, reason?: string) => {
    const res = await adminApi.put(
      `api/admin-entitlements/tenants/${encodeURIComponent(tenantId)}/landlord-class`,
      { landlordClass, reason: reason || null }
    );
    return normalizeTenantDetail({ ...res?.data?.data, tenantId });
  },

  setSubscriptionStatus: async (tenantId: string, subscriptionStatus: string, reason?: string) => {
    const res = await adminApi.put(
      `api/admin-entitlements/tenants/${encodeURIComponent(tenantId)}/subscription-status`,
      { subscriptionStatus, reason: reason || null }
    );
    return normalizeTenantDetail({ ...res?.data?.data, tenantId });
  },

  listRefundVoid: async (tenantId: string): Promise<{ entries: RefundVoidEntry[]; refundVoid: boolean; count: number }> => {
    const res = await adminApi.get(`api/admin-entitlements/tenants/${encodeURIComponent(tenantId)}/refund-void`);
    const data = (res?.data?.data || {}) as Record<string, unknown>;
    return {
      entries: ensureArray<RefundVoidEntry>(data.entries),
      refundVoid: data.refundVoid === true,
      count: asNumber(data.count) ?? 0,
    };
  },

  recordRefundVoid: async (tenantId: string, payload: { entitlementId: string; kind: string; note?: string }) => {
    const res = await adminApi.post(`api/admin-entitlements/tenants/${encodeURIComponent(tenantId)}/refund-void`, payload);
    return res?.data?.data;
  },

  /** Flag clear = adjudication on the ledger (entries remain immutable). */
  clearRefundVoidFlag: async (tenantId: string) => {
    const res = await adminApi.delete(`api/admin-entitlements/tenants/${encodeURIComponent(tenantId)}/refund-void`);
    return res?.data?.data;
  },
};
